import { FC, useEffect } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import campaignsDashboardSendToArchiveAlertButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_send_to_archive_alert_button_click';
import campaignsDashboardSendToArchiveAlertShown from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_send_to_archive_alert_shown';
import { Alert, Button, ButtonStack } from '@hh.ru/magritte-ui';
import { ExclamationCircleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { updateCampaignStatus } from 'components/CampaignsDashboard/utils/updateCampaignStatus';

import { CampaignFrontendStatus, setArchiveCampaignAlert } from 'store/models/campaignsDashboard';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'ArchiveCampaignAlert.title',
    description: 'ArchiveCampaignAlert.description',
    confirm: 'ArchiveCampaignAlert.button.confirm',
    cancel: 'ArchiveCampaignAlert.button.cancel',
};

export const ArchiveCampaignAlert: FC = () => {
    const statusChangeCampaignId = useSelector((state) => state.campaignsDashboard.statusChangeCampaignId);
    const isAlertVisible = useSelector((state) => state.campaignsDashboard.isArchiveCampaignAlertVisible);
    const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { abortRequest, abortSignal } = useRequestCancel();

    const handleClose = () => {
        abortRequest();
        dispatch(setArchiveCampaignAlert({ isVisible: false, campaignId: null }));
        setIsLoadingOff();
    };

    const handleConfirm = () => {
        abortRequest();

        if (statusChangeCampaignId) {
            setIsLoadingOn();

            campaignsDashboardSendToArchiveAlertButtonClick({ campaignId: statusChangeCampaignId ?? 0 });

            void dispatch(
                updateCampaignStatus(statusChangeCampaignId, CampaignFrontendStatus.Archived, abortSignal())
            ).finally(handleClose);
        } else {
            dispatch(addMagritteNotification(MagritteNotificationName.CampaignChangeStatusFailed));
            handleClose();
        }
    };

    useEffect(() => {
        return () => {
            abortRequest();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Alert
            visible={isAlertVisible}
            title={trls(TrlKeys.title)}
            description={trls(TrlKeys.description)}
            buttons={
                <ButtonStack stretched layout="vertical">
                    <Button mode="primary" style="negative" onClick={handleConfirm} loading={isLoading}>
                        {trls(TrlKeys.confirm)}
                    </Button>
                    <Button mode="secondary" style="neutral" onClick={handleClose}>
                        {trls(TrlKeys.cancel)}
                    </Button>
                </ButtonStack>
            }
            onClose={handleClose}
        >
            <ElementShownAnchor fn={campaignsDashboardSendToArchiveAlertShown} campaignId={statusChangeCampaignId ?? 0}>
                <ExclamationCircleFilledSize24
                    initialColor="negative"
                    backgroundStyle="negative-secondary"
                    padding={12}
                    borderRadius="halfHeight"
                />
            </ElementShownAnchor>
        </Alert>
    );
};
